/**
 * Client-side mirror of BGrav.Core.Helpers.LinkAccessibilityHelper.
 *
 * Classifies link destinations so React-rendered anchors announce the same behaviours, in the same
 * words, as the Razor-rendered ones. The two implementations must be kept in step; the C# side is
 * covered by BGrav.Core.Tests/Helpers/LinkAccessibilityHelperUnitTests.cs and this one by
 * LinkAccessibility.test.js.
 */

export const HINTS = {
    NEW_WINDOW: 'newWindow',
    PDF: 'pdf',
    DOWNLOAD: 'download',
    EMAIL: 'email',
    PHONE: 'phone',
};

/** Resource keys, shared with the server-side helper and seeded by AddLinkAccessibilityLocalization. */
export const HINT_RESOURCE_KEYS = {
    [HINTS.NEW_WINDOW]: 'Accessibility.Link.Hint.NewWindow',
    [HINTS.PDF]: 'Accessibility.Link.Hint.Pdf',
    [HINTS.DOWNLOAD]: 'Accessibility.Link.Hint.Download',
    [HINTS.EMAIL]: 'Accessibility.Link.Hint.Email',
    [HINTS.PHONE]: 'Accessibility.Link.Hint.Phone',
};

/** English text used when a resource has not been translated, matching the C# fallbacks. */
export const HINT_FALLBACKS = {
    [HINTS.NEW_WINDOW]: 'opens in a new window',
    [HINTS.PDF]: 'PDF',
    [HINTS.DOWNLOAD]: 'download',
    [HINTS.EMAIL]: 'opens your email application',
    [HINTS.PHONE]: 'calls this phone number',
};

export const WRAPPER_RESOURCE_KEYS = {
    prefix: 'Accessibility.Link.Hint.Prefix',
    suffix: 'Accessibility.Link.Hint.Suffix',
    separator: 'Accessibility.Link.Hint.Separator',
};

export const WRAPPER_FALLBACKS = {
    prefix: ' (',
    suffix: ')',
    separator: ', ',
};

export const BLANK_TARGET = '_blank';

const MAILTO_SCHEME = 'mailto:';
const TEL_SCHEME = 'tel:';

/**
 * Extensions the browser hands to a download or an external viewer rather than rendering as a page.
 * PDF is tracked separately because it gets its own, more specific hint.
 */
const DOWNLOAD_EXTENSIONS = [
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.csv',
    '.rtf',
    '.txt',
    '.zip',
    '.rar',
    '.7z',
];

/**
 * Order in which hints are announced: what the destination *is* before what opening it *does*.
 */
const HINT_ORDER = [HINTS.PDF, HINTS.DOWNLOAD, HINTS.EMAIL, HINTS.PHONE, HINTS.NEW_WINDOW];

/** Strips the query string and fragment so "/file.pdf?v=2" still matches as a PDF. */
function getPath(href) {
    const trimmed = (href || '').trim();
    const terminator = trimmed.search(/[?#]/);

    return terminator >= 0 ? trimmed.slice(0, terminator) : trimmed;
}

function endsWith(path, extension) {
    return path.toLowerCase().endsWith(extension);
}

/** True when the target attribute opens a new browsing context. */
export function opensInNewWindow(target) {
    return (target || '').trim().toLowerCase() === BLANK_TARGET;
}

/** True when the link points at a PDF document. */
export function isPdf(href) {
    return endsWith(getPath(href), '.pdf');
}

/** True when the link points at a non-PDF document the browser will download. */
export function isDownload(href) {
    const path = getPath(href);

    return DOWNLOAD_EXTENSIONS.some((extension) => endsWith(path, extension));
}

/**
 * Determines which behaviours of the supplied link need to be announced.
 *
 * @param {string} href The link destination. May be relative, absolute, or a protocol link.
 * @param {string} [target] The anchor target attribute.
 * @returns {string[]} Applicable hint keys, in announcement order. Empty when the link is ordinary.
 */
export function getLinkHints(href, target) {
    const trimmed = (href || '').trim().toLowerCase();

    // The protocol handoff is the whole story for mailto:/tel:. Announcing a new window on top of it
    // describes the browser's plumbing rather than what the user will experience, so the two are
    // mutually exclusive by design.
    if (trimmed.startsWith(MAILTO_SCHEME)) return [HINTS.EMAIL];
    if (trimmed.startsWith(TEL_SCHEME)) return [HINTS.PHONE];

    const hints = [];

    if (isPdf(href)) {
        hints.push(HINTS.PDF);
    } else if (isDownload(href)) {
        hints.push(HINTS.DOWNLOAD);
    }

    if (opensInNewWindow(target)) hints.push(HINTS.NEW_WINDOW);

    return HINT_ORDER.filter((hint) => hints.includes(hint));
}

/**
 * Builds the rel value for a link, adding noopener and noreferrer when it opens in a new window.
 * Any rel tokens already present (nofollow, sponsored, and so on) are preserved.
 *
 * @param {string} [target] The anchor target attribute.
 * @param {string} [existingRel] Rel tokens the caller has already decided on.
 * @returns {string} The merged rel value, or an empty string when no tokens apply.
 */
export function getLinkRel(target, existingRel) {
    const tokens = (existingRel || '').split(/\s+/).filter(Boolean);

    if (opensInNewWindow(target)) {
        ['noopener', 'noreferrer'].forEach((token) => {
            if (!tokens.some((existing) => existing.toLowerCase() === token)) tokens.push(token);
        });
    }

    return tokens.join(' ');
}

/**
 * Assembles the hint text from already-resolved translations.
 *
 * Kept separate from {@link getLinkHints} because translation lookup is a React hook concern; see
 * Common/components/ui/LinkHint.js for the rendering component.
 *
 * @param {string[]} hints Hint keys, as returned by getLinkHints.
 * @param {object} translations Map of hint key to translated text.
 * @param {object} [wrapper] Overrides for the prefix, suffix and separator.
 * @returns {string} The hint text including its leading space, or an empty string.
 */
export function formatLinkHint(hints, translations = HINT_FALLBACKS, wrapper = WRAPPER_FALLBACKS) {
    const parts = (hints || [])
        .map((hint) => translations[hint] || HINT_FALLBACKS[hint])
        .filter((part) => part && part.trim());

    if (!parts.length) return '';

    const { prefix, suffix, separator } = { ...WRAPPER_FALLBACKS, ...wrapper };

    return `${prefix}${parts.join(separator)}${suffix}`;
}

export default {
    HINTS,
    HINT_RESOURCE_KEYS,
    HINT_FALLBACKS,
    WRAPPER_RESOURCE_KEYS,
    WRAPPER_FALLBACKS,
    BLANK_TARGET,
    opensInNewWindow,
    isPdf,
    isDownload,
    getLinkHints,
    getLinkRel,
    formatLinkHint,
};
